/**
 * Spec 219 — bulk page persona-fit: choose, preview, confirm, download.
 *
 * The shape of this dialog is deliberate. It can queue sixty live judge calls,
 * so it never runs anything from the selection directly: it previews, shows the
 * exact matrix and what it will cost, and only then offers to run — and the
 * server refuses any matrix whose token does not match the one it previewed, so
 * the confirmation is a real gate rather than a courtesy.
 *
 * The estimate is NULL until some evaluation has recorded a cost, and it says
 * so in words. A guessed number would be worse than no number here, because it
 * is the figure someone decides to spend on.
 */
import { useState } from 'react';
import { Icon, Spinner } from './ui/Icon.js';
import { Button } from './ui/Button.js';
import {
  previewBulkPagePersonaFit,
  runBulkPagePersonaFit,
  getBulkPagePersonaFitReport,
  type BulkPreview,
} from '../api/pagePersonaEvaluations.js';
import type { Persona } from '../api/personas.js';

interface Props {
  brandId: string;
  personas: Persona[];
  /** Competitor domains for this brand, most-cited first. */
  domains: string[];
  onClose: () => void;
}

export function BulkPagePersonaFitModal({ brandId, personas, domains, onClose }: Props) {
  const [selDomains, setSelDomains] = useState<string[]>([]);
  const [urlsPer, setUrlsPer] = useState(3);
  const [selPersonas, setSelPersonas] = useState<string[]>([]);
  const [preview, setPreview] = useState<BulkPreview | null>(null);
  const [busy, setBusy] = useState<null | 'preview' | 'run' | 'report'>(null);
  const [error, setError] = useState<string | null>(null);
  const [ran, setRan] = useState<{ queued: number; skipped_existing: number } | null>(null);

  const selection = {
    domains: selDomains,
    urls_per_domain: urlsPer,
    persona_stable_ids: selPersonas,
  };
  const canPreview = selDomains.length > 0 && selPersonas.length > 0;

  function toggle(list: string[], v: string, set: (x: string[]) => void) {
    set(list.includes(v) ? list.filter((x) => x !== v) : [...list, v]);
  }

  async function doPreview() {
    setBusy('preview');
    setError(null);
    setRan(null);
    try {
      // Any change to the selection invalidates the previous token, which is
      // the point: you cannot preview one matrix and run another.
      setPreview(await previewBulkPagePersonaFit(brandId, selection));
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(null);
    }
  }

  async function doRun() {
    if (!preview) return;
    setBusy('run');
    setError(null);
    try {
      const cells = preview.cells
        .filter((c) => !c.already_complete)
        .map((c) => ({ url: c.url, persona_stable_id: c.persona_stable_id }));
      setRan(await runBulkPagePersonaFit(brandId, cells, preview.confirm_token));
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(null);
    }
  }

  async function doReport() {
    setBusy('report');
    setError(null);
    try {
      const { markdown, filename } = await getBulkPagePersonaFitReport(brandId, selection);
      const blob = new Blob([markdown], { type: 'text/markdown;charset=utf-8' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(null);
    }
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      data-testid="bulk-ppf-modal"
    >
      {/* max-h + flex-col + scrolling body: the matrix can be sixty rows and the
          action footer must stay reachable on a short viewport. */}
      <div className="flex max-h-[90vh] w-full max-w-3xl flex-col rounded-lg bg-white">
        <div className="flex items-center justify-between border-b border-slate-200 px-5 py-3">
          <h2 className="text-lg font-semibold text-slate-900">Run page persona-fit in bulk</h2>
          <Button round onClick={onClose} aria-label="Close">
            <Icon name="bx-x" />
          </Button>
        </div>

        <div className="flex-1 overflow-y-auto px-5 py-4">
          <p className="mb-4 text-sm text-slate-600">
            Pick competitor domains and personas. Their most-cited pages are evaluated for each
            persona. Nothing runs until you review the preview and confirm.
          </p>

          <div className="mb-4">
            <div className="mb-1.5 text-xs font-semibold text-slate-500">
              Competitor domains
            </div>
            <div className="flex flex-wrap gap-1.5">
              {domains.map((d) => (
                <button
                  key={d}
                  type="button"
                  data-testid="bulk-ppf-domain"
                  onClick={() => toggle(selDomains, d, setSelDomains)}
                  className={`rounded-full border px-2.5 py-1 text-xs ${
                    selDomains.includes(d)
                      ? 'border-violet-500 bg-violet-50 text-violet-700'
                      : 'border-slate-200 text-slate-600 hover:border-slate-300'
                  }`}
                >
                  {d}
                </button>
              ))}
              {domains.length === 0 && (
                <span className="text-xs text-slate-400">No competitor domains for this brand.</span>
              )}
            </div>
          </div>

          <div className="mb-4 flex items-center gap-2">
            <span className="text-xs font-semibold text-slate-500">
              Pages per domain
            </span>
            <input
              type="number"
              min={1}
              max={10}
              value={urlsPer}
              onChange={(e) => setUrlsPer(Math.max(1, Math.min(10, Number(e.target.value) || 1)))}
              data-testid="bulk-ppf-urls-per"
              className="w-16 rounded-md border border-slate-200 px-2 py-1 text-sm"
            />
            <span className="text-xs text-slate-400">most-cited pages, by how often each appeared as a source</span>
          </div>

          <div className="mb-4">
            <div className="mb-1.5 text-xs font-semibold text-slate-500">
              Personas
            </div>
            <div className="flex flex-wrap gap-1.5">
              {personas.map((p) => (
                <button
                  key={p.persona_id}
                  type="button"
                  data-testid="bulk-ppf-persona"
                  onClick={() => toggle(selPersonas, p.persona_id, setSelPersonas)}
                  className={`rounded-full border px-2.5 py-1 text-xs ${
                    selPersonas.includes(p.persona_id)
                      ? 'border-violet-500 bg-violet-50 text-violet-700'
                      : 'border-slate-200 text-slate-600 hover:border-slate-300'
                  }`}
                >
                  {p.name}
                </button>
              ))}
            </div>
          </div>

          {preview && (
            <div className="mt-4 rounded-md border border-slate-200" data-testid="bulk-ppf-preview">
              <div className="border-b border-slate-200 bg-slate-50 px-3 py-2 text-sm">
                <strong>{preview.to_run}</strong> evaluation(s) will run
                {preview.already_complete > 0 && (
                  <> · {preview.already_complete} already done and will be skipped</>
                )}
                {' · '}judge {preview.judge_model}
              </div>
              {/* The estimate is null on a database that has never recorded a
                  cost. Say that plainly rather than showing $0.00, which would
                  read as "free". */}
              <div className="flex items-start gap-2 border-b border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
                <Icon name="bx-error" className="mt-0.5 shrink-0" />
                <span data-testid="bulk-ppf-estimate">
                  {preview.estimated_cost_usd != null ? (
                    <>
                      Estimated cost <strong>${preview.estimated_cost_usd.toFixed(4)}</strong> —{' '}
                      {preview.estimate_basis}. This runs live models and spends real money.
                    </>
                  ) : (
                    <>
                      <strong>No cost estimate available:</strong> {preview.estimate_basis}. This
                      runs live models and spends real money — the first run has nothing to
                      estimate from, and later previews will.
                    </>
                  )}
                </span>
              </div>
              <div className="max-h-56 overflow-y-auto">
                <table className="hub-table">
                  <tbody>
                    {preview.cells.map((c) => (
                      <tr key={`${c.url}|${c.persona_stable_id}`}>
                        <td className="text-xs text-[var(--hub-color-ink-muted)]">{c.domain}</td>
                        <td className="text-xs">{c.url.replace(/^https?:\/\//, '').slice(0, 46)}</td>
                        <td className="is-num text-[var(--hub-color-ink-subtle)]">{c.mention_count}×</td>
                        <td className="text-right text-xs text-[var(--hub-color-ink-subtle)]">
                          {c.already_complete ? 'done' : 'will run'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <p className="border-t border-slate-100 px-3 py-2 text-xs leading-relaxed text-slate-400">
                {preview.url_measure}
              </p>
            </div>
          )}

          {ran && (
            <div className="mt-3 rounded-md bg-emerald-50 px-3 py-2 text-sm text-emerald-800" data-testid="bulk-ppf-queued">
              Queued {ran.queued}; skipped {ran.skipped_existing} already running or done. They
              process a few at a time — download the report whenever you like, it lists what is
              finished and what is not.
            </div>
          )}
          {error && (
            <div className="mt-3 rounded-md bg-red-50 px-3 py-2 text-sm text-red-700" data-testid="bulk-ppf-error">
              {error}
            </div>
          )}
        </div>

        <div className="flex items-center justify-end gap-2 border-t border-slate-200 px-5 py-3">
          <Button variant="ghost" onClick={onClose}>
            Close
          </Button>
          <Button variant="ghost" onClick={() => void doReport()} disabled={busy !== null || !canPreview} data-testid="bulk-ppf-report">
            {busy === 'report' ? <Spinner /> : 'Download report'}
          </Button>
          <Button variant="ghost" onClick={() => void doPreview()} disabled={busy !== null || !canPreview} data-testid="bulk-ppf-preview-btn">
            {busy === 'preview' ? <Spinner /> : 'Preview'}
          </Button>
          <Button
            variant="primary"
            onClick={() => void doRun()}
            disabled={busy !== null || !preview || preview.to_run === 0}
            data-testid="bulk-ppf-run"
          >
            {busy === 'run' ? (
              <Spinner />
            ) : (
              `Run ${preview?.to_run ?? 0} evaluation(s)`
            )}
          </Button>
        </div>
      </div>
    </div>
  );
}
